import type { BallPhase, BallState } from './BallState';
import type { WindField } from './WindField';
import { Aerodynamics } from './Aerodynamics';
import { BallIntegrator } from './BallIntegrator';
import { CollisionResolver, DEFAULT_COLLISION } from './CollisionResolver';
import { RollingModel } from './RollingModel';
import { PuttRollingModel } from './PuttRollingModel';
import type { TerrainSurfaceProbe } from './TerrainSurfaceProbe';

/** Coordinates replaceable shot-simulation modules. It owns no rendering and knows no golf rules. */
export class PhysicsEngine {
  private readonly aerodynamics = new Aerodynamics();
  private readonly integrator = new BallIntegrator();
  private readonly collision = new CollisionResolver(DEFAULT_COLLISION);
  private readonly rolling = new RollingModel(DEFAULT_COLLISION);
  private readonly puttRolling = new PuttRollingModel(DEFAULT_COLLISION);

  step(
    state: BallState,
    probe: TerrainSurfaceProbe,
    wind: WindField,
    deltaSeconds: number,
    elapsedSeconds: number,
  ): void {
    if (state.isResting) {
      const restingSample = probe.sample(state.position.x, state.position.z);
      state.position.y = restingSample.height + DEFAULT_COLLISION.radiusMeters;
      state.surfaceType = restingSample.properties.type;
      state.altitude = 0;
      state.phase = 'resting';
      return;
    }

    const dt = Math.min(deltaSeconds, 1 / 30);
    const steps = Math.max(1, Math.ceil(deltaSeconds / dt));
    const subDt = deltaSeconds / steps;

    for (let i = 0; i < steps; i++) {
      if (state.phase === 'rolling') {
        state.position.addScaledVector(state.velocity, subDt);
        const rollingSample = probe.sample(state.position.x, state.position.z);
        state.position.y = rollingSample.height + DEFAULT_COLLISION.radiusMeters;
        state.altitude = 0;
        if (state.motionMode === 'putt') this.puttRolling.apply(state, rollingSample, subDt);
        if (state.motionMode !== 'putt') this.rolling.apply(state, rollingSample, subDt);
        continue;
      }

      const acceleration = this.aerodynamics.acceleration(state, wind, elapsedSeconds);
      this.integrator.integrate(state, acceleration, subDt);
      this.aerodynamics.decaySpin(state, subDt);

      const sample = probe.sample(state.position.x, state.position.z);
      const contacted = this.collision.resolve(state, sample);
      const phaseAfterContact = state.phase as BallPhase;

      // If impact produced a real upward bounce, let the ball re-enter the
      // airborne integration path on the next substep instead of immediately
      // flattening it through the rolling model. This preserves visible first
      // bounces while keeping tuned rolling friction once the collision resolver
      // classifies the contact as rolling.
      if (contacted && phaseAfterContact === 'rolling') {
        state.motionMode = 'landingRoll';
        this.rolling.apply(state, sample, subDt);
      }
    }
  }
}
